import { useCallback, useEffect, useState } from 'react';
import type { Aviso, ListaAvisos, ResumenAvisos } from '../../api/operaciones';
import type { Gravedad, MiTipoAviso } from '../../api/ops/entregas';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Deshacer } from '../../componentes/Deshacer';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { PanelLateral } from '../../componentes/PanelLateral';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { estadoDeError } from '../../logica/estados';
import { enlaceDeReferencia } from '../../logica/navegacion';
import { haySiguiente, usePaginacionCursor } from '../../logica/paginacion';
import { useCabecera } from '../../marco/cabecera';
import { COLORES_TONO, lineaDeHora, textoSinAvisos } from '../../marco/avisosCampana';
import type { PropsPantalla } from '../tipos';
import { Menu, type OpcionMenu } from '../../componentes/Menu';
import { FILTROS_AVISOS, avisosDelFiltro, consultaDeFiltro, porDia, sePuedeSilenciar, type FiltroAvisos } from './reglas';
import { AYUDA_TUS_AVISOS, TONO, ICONO_GRAVEDAD } from './textos';

/**
 * «Tus avisos» (5.1, la página completa de la campana): los avisos **del rol** de quien mira (`listMyNotices`), por día,
 * con filtros sin cifras salvo «Sin leer» (`getMyNoticeSummary`, G121, G140) y «Ver los avisos» por gravedad. Cada aviso
 * con su color, `title`, `body`, cuándo, «4 veces», «Crítico · también por email» (`emailed`) y su enlace resuelto con
 * `link_targets` (G298: sin destino visible, sin enlace). Abrir el enlace lo marca como leído. Archivar y silenciar un
 * tipo van con «Deshacer» 5 s (G159); los críticos no se silencian (409). «Silenciados (n)» lista los tipos que me llegan
 * (`listMyNoticeTypes`, G297) para reactivar o silenciar sin esperar a un aviso. Son de cada persona: también se leen y
 * archivan con el negocio en solo lectura. Si Entregas aún no responde (404), se ve vacía, como la campana.
 */
const GRAVEDADES: readonly { valor: string; texto: string }[] = [
    { valor: '', texto: 'Todas' },
    { valor: 'critical', texto: 'Críticos' },
    { valor: 'warning', texto: 'Pendientes' },
    { valor: 'info', texto: 'Información' },
];

const VACIA: ListaAvisos = { data: [], next_cursor: null };

export function TusAvisos(props: PropsPantalla) {
    const { ops, negocio, movil, navegar, destinos = [], recargarAvisos } = props;
    const avisar = useAvisoBreve();
    const envio = useEnvioRetrasado();
    const [filtro, setFiltro] = useState<FiltroAvisos>(0);
    const [gravedad, setGravedad] = useState<Gravedad | null>(null);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    const [cargada, setCargada] = useState<{ vista: string; lista: ListaAvisos | null; error: unknown } | null>(null);
    const [resumen, setResumen] = useState<ResumenAvisos | null>(null);
    const [tipos, setTipos] = useState<MiTipoAviso[] | null>(null);
    const [tiposLeidos, setTiposLeidos] = useState(false);
    const [ocultos, setOcultos] = useState<ReadonlySet<string>>(new Set());
    const [silenciados, setSilenciados] = useState(false);
    const [intento, setIntento] = useState(0);

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
        recargarAvisos?.();
    }, [recargarAvisos]);

    const consulta = consultaDeFiltro(filtro, gravedad);
    const vista = `${JSON.stringify(consulta)}|${pag.cursor ?? ''}|${String(intento)}`;
    useEffect(() => {
        let vivo = true;
        ops.listMyNotices(negocio, { ...consulta, cursor: pag.cursor ?? undefined })
            .then((l) => {
                if (vivo) {
                    setCargada({ vista, lista: l, error: null });
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    // Entregas aún no responde (404): sin avisos y sin error, como la campana.
                    const sinModulo = e instanceof ErrorApi && e.estado === 404;
                    setCargada({ vista, lista: sinModulo ? VACIA : null, error: sinModulo ? null : e });
                }
            });
        return () => {
            vivo = false;
        };
        // `consulta` es nueva en cada pintado; la vista la resume.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ops, negocio, vista]);

    useEffect(() => {
        let vivo = true;
        ops.getMyNoticeSummary(negocio)
            .then((r) => {
                if (vivo) {
                    setResumen(r);
                }
            })
            .catch(() => undefined);
        ops.listMyNoticeTypes(negocio)
            .then((l) => {
                if (vivo) {
                    setTipos(l.data);
                    setTiposLeidos(true);
                }
            })
            .catch(() => {
                if (vivo) {
                    setTipos([]);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento]);

    const sobre = (accion: () => Promise<unknown>, error: string) => {
        accion()
            .then(recargar)
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: e instanceof ErrorApi && e.estado === 409 ? 'Los avisos críticos no se pueden silenciar.' : error });
                recargar();
            });
    };

    const leerTodos = () => {
        sobre(() => ops.markAllNoticesRead(negocio), 'No he podido marcar los avisos como leídos. Prueba de nuevo.');
    };
    const nSilenciados = (tipos ?? []).filter((t) => t.muted).length;

    useCabecera({
        ayuda: AYUDA_TUS_AVISOS,
        acciones: [
            { clave: 'silenciados', texto: `Silenciados (${String(nSilenciados)})`, icono: 'bell-off', secundaria: true, onClick: () => { setSilenciados(true); } },
            { clave: 'leer-todos', texto: 'Marcar todo como leído', icono: 'check-check', secundaria: true, desactivado: (resumen?.unread ?? 0) === 0, onClick: leerTodos },
        ],
    });

    const tipoDe = (codigo: string) => tipos?.find((t) => t.notice_type === codigo);
    const silenciable = (a: Aviso) => {
        const t = tipoDe(a.notice_type);
        return t === undefined ? false : sePuedeSilenciar(t) && !t.muted;
    };

    const archivar = (a: Aviso) => {
        setOcultos((o) => new Set([...o, a.id]));
        envio.programar({
            texto: 'Aviso archivado.',
            donde: 'lista',
            enviar: () => {
                sobre(() => ops.archiveNotice(negocio, a.id), 'No he podido archivar el aviso. Prueba de nuevo.');
            },
            alDeshacer: () => {
                setOcultos((o) => new Set([...o].filter((x) => x !== a.id)));
            },
        });
    };
    const silenciar = (codigo: string, titulo: string) => {
        setTipos((ts) => (ts ?? []).map((t) => (t.notice_type === codigo ? { ...t, muted: true } : t)));
        envio.programar({
            texto: `Ya no recibirás «${titulo}».`,
            donde: 'lista',
            enviar: () => {
                sobre(() => ops.muteNoticeType(negocio, codigo), 'No he podido silenciar el aviso. Prueba de nuevo.');
            },
            alDeshacer: () => {
                setTipos((ts) => (ts ?? []).map((t) => (t.notice_type === codigo ? { ...t, muted: false } : t)));
            },
        });
    };
    const reactivar = (codigo: string) => {
        sobre(() => ops.unmuteNoticeType(negocio, codigo), 'No he podido reactivar el aviso. Prueba de nuevo.');
    };
    const abrir = (a: Aviso, ruta: string) => {
        if (a.state === 'unread') {
            ops.markNoticeRead(negocio, a.id).then(() => recargarAvisos?.()).catch(() => undefined);
        }
        navegar(ruta);
    };

    const actual = cargada?.vista === vista ? cargada : null;
    if (actual?.error != null && actual.lista === null) {
        return <EstadoError seccion="tus avisos" codigoSoporte={estadoDeError(actual.error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    const lista = actual?.lista ?? null;
    const avisos = lista === null ? [] : avisosDelFiltro(filtro, lista.data).filter((a) => !ocultos.has(a.id));

    return (
        <div className="rv-ca-apartado">
            <div className="rv-en-filtros">
                <Filtros
                    etiqueta="Qué avisos ver"
                    items={FILTROS_AVISOS.map((etiqueta, i) => ({ etiqueta, cifra: i === 1 ? (resumen?.unread ?? null) : null }))}
                    valor={filtro}
                    onCambio={(i) => {
                        setFiltro(i as FiltroAvisos);
                        reiniciar();
                    }}
                />
                <div className="rv-en-filtros__gravedad">
                    <Desplegable
                        etiqueta="Ver los avisos"
                        valor={gravedad ?? ''}
                        opciones={GRAVEDADES}
                        onChange={(v) => {
                            setGravedad(v === '' ? null : (v as Gravedad));
                            reiniciar();
                        }}
                    />
                </div>
            </div>
            {envio.pendiente !== null && (
                <div className="rv-ca-hecho" role="status">
                    <span className="rv-ca-hecho__baldosa"><Icono nombre="check" /></span>
                    <span style={{ flex: 1 }}>{envio.pendiente.texto}</span>
                    <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                </div>
            )}
            {lista === null ? (
                <EstadoCargando seccion="tus avisos" />
            ) : avisos.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa"><Icono nombre="bell" /></span>
                    <strong>{filtro === 0 ? textoSinAvisos(tiposLeidos && tipos !== null && tipos.length === 0) : filtro === 1 ? 'Lo tienes todo leído' : filtro === 2 ? 'No has archivado ningún aviso' : 'Ningún aviso resuelto'}</strong>
                    {filtro === 0 && <span>Aquí te avisamos de lo que necesita tu atención: canales caídos, pagos, reservas por completar…</span>}
                </div>
            ) : (
                porDia(avisos).map((g) => (
                    <section key={g.titulo} className="rv-en-dia" aria-label={g.titulo}>
                        <h2 className="rv-en-dia__t">{g.titulo}</h2>
                        {g.avisos.map((a) => {
                            const enlace = a.link == null ? null : enlaceDeReferencia(destinos, a.link.type, a.link.id, a.link_label);
                            const opciones: OpcionMenu[] = [
                                ...(a.state === 'unread' ? [{ clave: 'leer', texto: 'Marcar como leído', icono: 'check' as const, onClick: () => { sobre(() => ops.markNoticeRead(negocio, a.id), 'No he podido marcarlo como leído.'); } }] : []),
                                ...(a.state !== 'archived' ? [{ clave: 'archivar', texto: 'Archivar', icono: 'archive' as const, onClick: () => { archivar(a); } }] : []),
                                ...(silenciable(a) ? [{ clave: 'silenciar', texto: 'Silenciar este tipo de aviso', icono: 'bell-off' as const, onClick: () => { silenciar(a.notice_type, tipoDe(a.notice_type)?.title ?? a.title ?? ''); } }] : []),
                            ];
                            return (
                                <TarjetaDeAviso
                                    key={a.id}
                                    aviso={a}
                                    enlace={enlace}
                                    movil={movil}
                                    opciones={opciones}
                                    onAbrir={(ruta) => { abrir(a, ruta); }}
                                    onLeer={() => { sobre(() => ops.markNoticeRead(negocio, a.id), 'No he podido marcarlo como leído.'); }}
                                    onArchivar={() => { archivar(a); }}
                                />
                            );
                        })}
                    </section>
                ))
            )}
            {lista !== null && (pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
            )}
            <PanelLateral abierto={silenciados} onCerrar={() => { setSilenciados(false); }} movil={movil} titulo="Silenciados" eyebrow="Tus avisos" icono="bell-off" subtitulo="Los avisos que no quieres recibir. Los críticos llegan siempre.">
                <Silenciados tipos={tipos} onReactivar={reactivar} onSilenciar={(t) => { silenciar(t.notice_type, t.title); }} />
            </PanelLateral>
        </div>
    );
}

function TarjetaDeAviso({ aviso: a, enlace, movil, opciones, onAbrir, onLeer, onArchivar }: { aviso: Aviso; enlace: { ruta: string; etiqueta: string } | null; movil: boolean; opciones: OpcionMenu[]; onAbrir: (ruta: string) => void; onLeer: () => void; onArchivar: () => void }) {
    const tono = TONO[a.severity];
    // Los no leídos, en pastilla de su color; los leídos, en blanco.
    const c = a.state === 'unread' ? COLORES_TONO[tono] : COLORES_TONO.info;
    const fondo = a.state === 'unread' ? c.fondo : 'var(--rv-white)';
    return (
        <article className={`rv-en-aviso${a.state === 'unread' ? '' : ' rv-en-aviso--leido'}`} style={{ background: fondo, color: c.tinta }} aria-label={a.title ?? 'Aviso'}>
            <span className="rv-en-aviso__baldosa" style={{ background: a.state === 'unread' ? c.baldosa : 'var(--rv-paper)' }}>
                <Icono nombre={ICONO_GRAVEDAD[a.severity]} />
            </span>
            <div className="rv-en-aviso__textos">
                <strong>{a.title}</strong>
                {a.body != null && a.body !== '' && <span style={{ color: c.sub }}>{a.body}</span>}
                <small style={{ color: c.sub }}>
                    <Icono nombre="clock" />
                    {lineaDeHora(a)}
                </small>
                {enlace !== null && (
                    <button type="button" className="rv-en-aviso__enlace" onClick={() => { onAbrir(enlace.ruta); }}>
                        {enlace.etiqueta}
                        <Icono nombre="arrow-right" />
                    </button>
                )}
            </div>
            <div className="rv-en-aviso__acciones">
                {a.state === 'unread' ? (
                    <Boton tipo="secundario" icono="check" movil={movil} onClick={onLeer}>Marcar como leído</Boton>
                ) : a.state === 'read' ? (
                    <Boton tipo="secundario" icono="archive" movil={movil} onClick={onArchivar}>Archivar</Boton>
                ) : null}
                <Menu etiqueta={`Más acciones de «${a.title ?? 'este aviso'}»`} movil={movil} opciones={opciones.filter((o) => (a.state === 'unread' ? o.clave !== 'leer' : o.clave !== 'archivar'))} />
            </div>
        </article>
    );
}

function Silenciados({ tipos, onReactivar, onSilenciar }: { tipos: MiTipoAviso[] | null; onReactivar: (codigo: string) => void; onSilenciar: (t: MiTipoAviso) => void }) {
    if (tipos === null) {
        return <EstadoCargando seccion="tus tipos de aviso" />;
    }
    const callados = tipos.filter((t) => t.muted);
    const otros = tipos.filter((t) => !t.muted);
    return (
        <div className="rv-ca-cuerpo">
            <div className="rv-ca-bloque">
                <div className="rv-ca-bloque__cab"><strong>Silenciados</strong></div>
                {callados.length === 0 ? (
                    <p className="rv-ca-nota rv-ca-nota--gris">No has silenciado ningún aviso.</p>
                ) : (
                    callados.map((t) => (
                        <div key={t.notice_type} className="rv-ca-dato">
                            <span>{t.title}</span>
                            <Boton tipo="secundario" icono="bell-ring" onClick={() => { onReactivar(t.notice_type); }}>Reactivar</Boton>
                        </div>
                    ))
                )}
            </div>
            <div className="rv-ca-bloque">
                <div className="rv-ca-bloque__cab"><strong>Los que te llegan</strong><span>Puedes silenciar un tipo sin esperar a que llegue.</span></div>
                {otros.map((t) => (
                    <div key={t.notice_type} className="rv-ca-dato">
                        <span>{t.title}{t.description != null && <small className="rv-en-desc">{t.description}</small>}</span>
                        {sePuedeSilenciar(t) ? (
                            <Boton tipo="secundario" icono="bell-off" onClick={() => { onSilenciar(t); }}>Silenciar</Boton>
                        ) : (
                            <strong className="rv-en-siempre"><Icono nombre="lock" />{t.severity === 'critical' ? 'Crítico · no se silencia' : 'No se silencia'}</strong>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
